import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout, AuthLayout } from './components/Layout';
import { Login, Register, ForgotPassword, ResetPassword, PrivateRoute } from './components/Auth';
import { PublicLayout, LandingPage, PricingPage } from './components/Marketing';
import { ChatPage } from './components/Chat';
import KnowledgeBasePage from './components/KnowledgeBase/KnowledgeBasePage';
import TrainerPage from './components/Trainer/TrainerPage';
import ConversationsPage from './components/Conversations/ConversationsPage';
import LeadsPage from './components/CRM/LeadsPage';
import HelpCenter from './components/Help/HelpCenter';
import DashboardPage from './components/Dashboard/DashboardPage';
import WidgetSettingsPage from './components/WidgetSettingsPage';
import SettingsPage from './components/Settings/SettingsPage';
import ApiTester from './components/Debug/ApiTester';
import AdminPage from './components/Admin/AdminPage';
import RequirePlatformAdmin from './components/Admin/RequirePlatformAdmin';
import { AuthProvider } from './contexts/AuthContext';
import authService from './services/auth';

// Code-split the Analytics page so it never weighs down the initial load.
const AnalyticsPage = React.lazy(() => import('./components/Analytics/AnalyticsPage'));

/**
 * Dashboard component with metrics and activity feed
 */
const Dashboard = () => <DashboardPage />;

/**
 * Chat component with modern glass-morphism UI
 */
const Chat = () => <ChatPage />;

/**
 * Voice Assistant component placeholder
 */
const VoiceAssistant = () => (
  <div className="bg-gray-800 border border-gray-700 shadow-lg rounded-lg p-6 text-gray-200">
    <h1 className="text-2xl font-bold mb-4 text-white">Voice Assistant</h1>
    <p className="text-gray-400">Voice assistant interface will be implemented here.</p>
  </div>
);

/**
 * About component
 */
const About = () => (
  <div className="bg-gray-800 border border-gray-700 shadow-lg rounded-lg p-6 text-gray-200">
    <h1 className="text-2xl font-bold mb-4 text-white">About SageMindLabs</h1>
    <p className="mb-4">
      SageMindLabs is a self-hostable, knowledge-grounded AI agent platform. Point it at any website
      or upload your documents, and it learns from your content — then answers with citations,
      takes actions via tool calling, and runs a ReAct agentic loop to resolve multi-step goals.
    </p>
    <p>
      A built-in Trainer generates and validates Q&A pairs, collects feedback, and fine-tunes the
      agent so it keeps getting smarter. Deploy it as a dashboard, an embeddable widget, or via voice
      (LiveKit). You own your knowledge base and lead graph — no vendor lock-in.
    </p>
  </div>
);

/**
 * Main App component with routing configuration
 */
function App() {
  // Check if user is already logged in for initial redirect
  const isLoggedIn = authService.isLoggedIn();
  
  console.log('App rendering, isLoggedIn:', isLoggedIn);
  
  return (
    <Router>
      <AuthProvider>
      <Routes>
        {/* Public marketing site (no sidebar, own nav/footer) */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/:slug" element={<HelpCenter />} />
        </Route>

        {/* Auth routes with AuthLayout (no sidebar) */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        {/* Public about page with main layout */}
        <Route path="/about" element={<Layout />}>
          <Route index element={<About />} />
        </Route>
        
        {/* Debug route for API testing */}
        <Route path="/debug" element={<Layout />}>
          <Route index element={<ApiTester />} />
        </Route>
        
        {/* Protected routes with main layout (includes sidebar) */}
        <Route element={<PrivateRoute />}>
          <Route path="/" element={<Layout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="analytics"
              element={
                <Suspense fallback={<div className="p-6 text-gray-400">Loading analytics…</div>}>
                  <AnalyticsPage />
                </Suspense>
              }
            />
            <Route path="chat" element={<Chat />} />
            <Route path="voice" element={<VoiceAssistant />} />
            <Route path="knowledge" element={<KnowledgeBasePage />} />
            <Route path="trainer" element={<TrainerPage />} />
            <Route path="conversations" element={<ConversationsPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/widget" element={<WidgetSettingsPage />} />
            <Route
              path="admin"
              element={
                <RequirePlatformAdmin>
                  <AdminPage />
                </RequirePlatformAdmin>
              }
            />
          </Route>
        </Route>

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to={isLoggedIn ? "/dashboard" : "/login"} replace />} />
      </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
